import { useInfiniteQuery } from "@tanstack/react-query";

export interface Opportunity {
  id: string;
  name: string;
  community_need: string;
  volunteer_support: string;
  impact: string;
  beneficiary_types: string[];
  relevant_un_day: string[];
  focus_area: string;
  location: string;
  format: string;
  outcome: string;
  image_url: string | null;
}

export interface OpportunitiesResponse {
  data: Opportunity[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  message: string;
}

interface UseOpportunitiesApiProps {
  limit?: number;
  focusArea?: string[];
  beneficiaryType?: string[];
  format?: string[];
  location?: string;
  searchQuery?: string;
}

export function useOpportunitiesApi({
  limit = 10,
  focusArea = [],
  beneficiaryType = [],
  format = [],
  location,
  searchQuery,
}: UseOpportunitiesApiProps = {}) {
  return useInfiniteQuery({
    queryKey: [
      "opportunities",
      {
        limit,
        focusArea,
        beneficiaryType,
        format,
        location,
        searchQuery,
      },
    ],
    queryFn: async ({ pageParam = 1 }) => {
      const params = new URLSearchParams();

      // Add pagination parameters
      params.append('page', pageParam.toString());
      params.append('limit', limit.toString());

      // Add filter parameters
      if (focusArea.length > 0) params.append('focus_area', focusArea.join(','));
      if (beneficiaryType.length > 0) params.append('beneficiary_type', beneficiaryType.join(','));
      if (format.length > 0) params.append('format', format.join(','));
      if (location) params.append('location', location);
      if (searchQuery) params.append('search', searchQuery);

      const response = await fetch(`${import.meta.env.VITE_CC_API_URL}/v1/opportunities/?${params.toString()}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json() as OpportunitiesResponse;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { pagination } = lastPage;
      return pagination.page < pagination.totalPages ? pagination.page + 1 : undefined;
    },
  });
}
